<template>
  <div class="max-w-9xl mx-auto p-6">
    <h1 class="text-3xl font-bold text-gray-800 mb-8">
      Carrier Services Management
    </h1>

    <div>
      <!-- Filters -->
      <div class="mb-6 bg-gray-50 p-4 rounded-lg">
        <h3 class="text-lg font-medium text-gray-900 mb-4">Filters</h3>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <!-- Carrier Code Filter -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Carrier Code
            </label>
            <input
              v-model="filters.carrierCode"
              type="text"
              placeholder="Filter by carrier code..."
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <!-- NKR Enabled Filter -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">
              NKR Enabled
            </label>
            <select
              v-model="filters.nkrEnabled"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All</option>
              <option value="true">Enabled</option>
              <option value="false">Disabled</option>
            </select>
          </div>

          <!-- Web Self Service Filter -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Web Self Service
            </label>
            <select
              v-model="filters.webSelfServiceEnabled"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All</option>
              <option value="true">Enabled</option>
              <option value="false">Disabled</option>
            </select>
          </div>

          <!-- Special Order Filter -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Special Order
            </label>
            <select
              v-model="filters.specialOrderEnabled"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All</option>
              <option value="true">Enabled</option>
              <option value="false">Disabled</option>
            </select>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="mt-4 flex space-x-3">
          <button
            @click="clearFilters"
            class="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Clear All Filters
          </button>
          <button
            @click="updateCarriersAndServices"
            :disabled="updatingServices"
            class="px-4 py-2 text-sm text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span v-if="updatingServices">Updating Services...</span>
            <span v-else>Get New Services from ShipStation</span>
          </button>
        </div>

        <!-- Results Count -->
        <div v-if="services?.data" class="mt-4 text-sm text-gray-600">
          Showing {{ filteredAndSortedServices.length }} of
          {{ services.data.length }} services
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="flex justify-center py-8">
        <div
          class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"
        ></div>
      </div>

      <!-- Error State -->
      <div
        v-else-if="error"
        class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4"
      >
        <strong>Error:</strong> {{ error }}
      </div>

      <!-- Services Table -->
      <div
        v-else-if="filteredAndSortedServices.length > 0"
        class="bg-white shadow-md rounded-lg overflow-hidden"
      >
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th
                @click="sort('name')"
                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
              >
                <div class="flex items-center">
                  Service Name
                  <span v-if="sortBy === 'name'" class="ml-1">
                    {{ sortOrder === "asc" ? "↑" : "↓" }}
                  </span>
                </div>
              </th>
              <th
                @click="sort('code')"
                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
              >
                <div class="flex items-center">
                  Code
                  <span v-if="sortBy === 'code'" class="ml-1">
                    {{ sortOrder === "asc" ? "↑" : "↓" }}
                  </span>
                </div>
              </th>
              <th
                @click="sort('carrierCode')"
                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
              >
                <div class="flex items-center">
                  Carrier Code
                  <span v-if="sortBy === 'carrierCode'" class="ml-1">
                    {{ sortOrder === "asc" ? "↑" : "↓" }}
                  </span>
                </div>
              </th>
              <th
                @click="sort('shippingProviderId')"
                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
              >
                <div class="flex items-center">
                  Provider ID
                  <span v-if="sortBy === 'shippingProviderId'" class="ml-1">
                    {{ sortOrder === "asc" ? "↑" : "↓" }}
                  </span>
                </div>
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Service Type
              </th>
              <th
                @click="sort('nkrEnabled')"
                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
              >
                <div class="flex items-center">
                  NKR Enabled
                  <span v-if="sortBy === 'nkrEnabled'" class="ml-1">
                    {{ sortOrder === "asc" ? "↑" : "↓" }}
                  </span>
                </div>
              </th>
              <th
                @click="sort('webSelfServiceEnabled')"
                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
              >
                <div class="flex items-center">
                  Web Self Service
                  <span v-if="sortBy === 'webSelfServiceEnabled'" class="ml-1">
                    {{ sortOrder === "asc" ? "↑" : "↓" }}
                  </span>
                </div>
              </th>
              <th
                @click="sort('specialOrderEnabled')"
                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
              >
                <div class="flex items-center">
                  Special Order
                  <span v-if="sortBy === 'specialOrderEnabled'" class="ml-1">
                    {{ sortOrder === "asc" ? "↑" : "↓" }}
                  </span>
                </div>
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr
              v-for="(service, index) in filteredAndSortedServices"
              :key="service.uuid"
              :class="{ 'bg-gray-50': index % 2 === 1 }"
            >
              <td
                class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
              >
                {{ service.name || "N/A" }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {{ service.code || "N/A" }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {{ service.carrierCode || "N/A" }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {{ service.shippingProviderId || "N/A" }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                <div class="flex space-x-2">
                  <span
                    v-if="service.domestic"
                    class="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800"
                  >
                    Domestic
                  </span>
                  <span
                    v-if="service.international"
                    class="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800"
                  >
                    International
                  </span>
                  <span
                    v-if="!service.domestic && !service.international"
                    class="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800"
                  >
                    None
                  </span>
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <span
                  :class="{
                    'bg-green-100 text-green-800': service.nkrEnabled,
                    'bg-red-100 text-red-800': !service.nkrEnabled
                  }"
                  class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
                >
                  {{ service.nkrEnabled ? "Enabled" : "Disabled" }}
                </span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <span
                  :class="{
                    'bg-green-100 text-green-800':
                      service.webSelfServiceEnabled,
                    'bg-red-100 text-red-800': !service.webSelfServiceEnabled
                  }"
                  class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
                >
                  {{ service.webSelfServiceEnabled ? "Enabled" : "Disabled" }}
                </span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <span
                  :class="{
                    'bg-green-100 text-green-800': service.specialOrderEnabled,
                    'bg-red-100 text-red-800': !service.specialOrderEnabled
                  }"
                  class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
                >
                  {{ service.specialOrderEnabled ? "Enabled" : "Disabled" }}
                </span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                <button
                  @click="editService(service)"
                  class="text-blue-600 hover:text-blue-900 mr-4"
                >
                  Edit
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- No Services or No Filtered Results -->
      <div v-else-if="!loading" class="text-center py-8 text-gray-500">
        <div v-if="!services?.data?.length">
          No carrier services found.
        </div>
        <div v-else-if="filteredAndSortedServices.length === 0">
          <p>No services match the current filters.</p>
          <button
            @click="clearFilters"
            class="mt-2 px-4 py-2 text-sm text-blue-600 hover:text-blue-800 underline"
          >
            Clear all filters
          </button>
        </div>
      </div>
    </div>

    <!-- Edit Service Modal -->
    <div
      v-if="showEditModal"
      class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50"
      @click="closeEditModal"
    >
      <div
        class="relative top-20 mx-auto p-6 border w-96 shadow-lg rounded-md bg-white"
        @click.stop
      >
        <div class="mt-3">
          <h3 class="text-lg font-medium text-gray-900 mb-4">
            Edit Service: {{ editingService?.name }}
          </h3>

          <div class="space-y-4">
            <!-- Service Info -->
            <div class="text-sm text-gray-600 mb-4">
              <p><strong>Code:</strong> {{ editingService?.code }}</p>
              <p>
                <strong>Carrier Code:</strong> {{ editingService?.carrierCode }}
              </p>
            </div>

            <!-- NKR Enabled Toggle -->
            <div class="flex items-center justify-between">
              <label class="text-sm font-medium text-gray-700">
                NKR Enabled
              </label>
              <div class="relative">
                <input
                  v-model="editForm.nkrEnabled"
                  type="checkbox"
                  class="sr-only"
                  :disabled="saving"
                />
                <div
                  @click="
                    !saving && (editForm.nkrEnabled = !editForm.nkrEnabled)
                  "
                  :class="{
                    'bg-blue-600': editForm.nkrEnabled,
                    'bg-gray-200': !editForm.nkrEnabled,
                    'cursor-not-allowed opacity-50': saving,
                    'cursor-pointer': !saving
                  }"
                  class="block w-12 h-6 rounded-full transition-colors duration-200"
                >
                  <div
                    :class="{
                      'translate-x-6': editForm.nkrEnabled,
                      'translate-x-0': !editForm.nkrEnabled
                    }"
                    class="dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform duration-200"
                  ></div>
                </div>
              </div>
            </div>

            <!-- Web Self Service Enabled Toggle -->
            <div class="flex items-center justify-between">
              <label class="text-sm font-medium text-gray-700">
                Web Self Service Enabled
              </label>
              <div class="relative">
                <input
                  v-model="editForm.webSelfServiceEnabled"
                  type="checkbox"
                  class="sr-only"
                  :disabled="saving"
                />
                <div
                  @click="
                    !saving &&
                      (editForm.webSelfServiceEnabled = !editForm.webSelfServiceEnabled)
                  "
                  :class="{
                    'bg-blue-600': editForm.webSelfServiceEnabled,
                    'bg-gray-200': !editForm.webSelfServiceEnabled,
                    'cursor-not-allowed opacity-50': saving,
                    'cursor-pointer': !saving
                  }"
                  class="block w-12 h-6 rounded-full transition-colors duration-200"
                >
                  <div
                    :class="{
                      'translate-x-6': editForm.webSelfServiceEnabled,
                      'translate-x-0': !editForm.webSelfServiceEnabled
                    }"
                    class="dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform duration-200"
                  ></div>
                </div>
              </div>
            </div>

            <!-- Special Order Enabled Toggle -->
            <div class="flex items-center justify-between">
              <label class="text-sm font-medium text-gray-700">
                Special Order Enabled
              </label>
              <div class="relative">
                <input
                  v-model="editForm.specialOrderEnabled"
                  type="checkbox"
                  class="sr-only"
                  :disabled="saving"
                />
                <div
                  @click="
                    !saving &&
                      (editForm.specialOrderEnabled = !editForm.specialOrderEnabled)
                  "
                  :class="{
                    'bg-blue-600': editForm.specialOrderEnabled,
                    'bg-gray-200': !editForm.specialOrderEnabled,
                    'cursor-not-allowed opacity-50': saving,
                    'cursor-pointer': !saving
                  }"
                  class="block w-12 h-6 rounded-full transition-colors duration-200"
                >
                  <div
                    :class="{
                      'translate-x-6': editForm.specialOrderEnabled,
                      'translate-x-0': !editForm.specialOrderEnabled
                    }"
                    class="dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform duration-200"
                  ></div>
                </div>
              </div>
            </div>
          </div>

          <!-- Modal Actions -->
          <div class="flex justify-end space-x-3 mt-6">
            <button
              @click="closeEditModal"
              :disabled="saving"
              class="px-4 py-2 text-sm text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              @click="saveService"
              :disabled="saving"
              class="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              <span v-if="saving">Saving...</span>
              <span v-else>Save Changes</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getCarrierServices,
  updateCarrierService,
  updateCarriersAndServicesFromShipStation
} from "@/api/CarrierServicesService";

export default {
  name: "CarrierServices",
  data() {
    return {
      services: null,
      loading: false,
      error: null,
      filters: {
        carrierCode: "",
        nkrEnabled: "",
        webSelfServiceEnabled: "",
        specialOrderEnabled: ""
      },
      sortBy: "",
      sortOrder: "asc", // 'asc' or 'desc'
      showEditModal: false,
      editingService: null,
      editForm: {
        nkrEnabled: false,
        webSelfServiceEnabled: false,
        specialOrderEnabled: false
      },
      saving: false,
      updatingServices: false
    };
  },
  computed: {
    filteredAndSortedServices() {
      if (!this.services?.data) return [];

      let filtered = this.services.data.filter(service => {
        // Filter by carrier code
        if (
          this.filters.carrierCode &&
          !service.carrierCode
            ?.toLowerCase()
            .includes(this.filters.carrierCode.toLowerCase())
        ) {
          return false;
        }

        // Filter by NKR enabled
        if (
          this.filters.nkrEnabled !== "" &&
          service.nkrEnabled !== (this.filters.nkrEnabled === "true")
        ) {
          return false;
        }

        // Filter by Web Self Service enabled
        if (
          this.filters.webSelfServiceEnabled !== "" &&
          service.webSelfServiceEnabled !==
            (this.filters.webSelfServiceEnabled === "true")
        ) {
          return false;
        }

        // Filter by Special Order enabled
        if (
          this.filters.specialOrderEnabled !== "" &&
          service.specialOrderEnabled !==
            (this.filters.specialOrderEnabled === "true")
        ) {
          return false;
        }

        return true;
      });

      // Sort if sortBy is set
      if (this.sortBy) {
        filtered.sort((a, b) => {
          let aValue = a[this.sortBy];
          let bValue = b[this.sortBy];

          // Handle null/undefined values
          if (aValue == null) aValue = "";
          if (bValue == null) bValue = "";

          // Convert to string for consistent comparison
          aValue = String(aValue).toLowerCase();
          bValue = String(bValue).toLowerCase();

          if (this.sortOrder === "asc") {
            return aValue.localeCompare(bValue);
          } else {
            return bValue.localeCompare(aValue);
          }
        });
      }

      return filtered;
    }
  },
  mounted() {
    this.loadCarrierServices();
  },
  methods: {
    async loadCarrierServices() {
      this.loading = true;
      this.error = null;
      try {
        this.services = await getCarrierServices();
        console.log("Carrier Services:", this.services);
      } catch (error) {
        this.error = error.message || "Failed to load carrier services";
        console.error("Error fetching carrier services:", error);
      } finally {
        this.loading = false;
      }
    },

    editService(service) {
      this.editingService = service;
      this.editForm = {
        nkrEnabled: service.nkrEnabled,
        webSelfServiceEnabled: service.webSelfServiceEnabled,
        specialOrderEnabled: service.specialOrderEnabled
      };
      this.showEditModal = true;
    },

    async toggleServiceStatus(service) {
      // TODO: Implement toggle functionality
      console.log("Toggle service status:", service);
      this.$toast.info(
        `Toggle functionality for ${service.name} not implemented yet.`
      );
    },

    sort(field) {
      if (this.sortBy === field) {
        // Toggle sort order if clicking the same field
        this.sortOrder = this.sortOrder === "asc" ? "desc" : "asc";
      } else {
        // Set new field and default to ascending
        this.sortBy = field;
        this.sortOrder = "asc";
      }
    },

    clearFilters() {
      this.filters = {
        carrierCode: "",
        nkrEnabled: "",
        webSelfServiceEnabled: "",
        specialOrderEnabled: ""
      };
      this.sortBy = "";
      this.sortOrder = "asc";
    },

    closeEditModal() {
      this.showEditModal = false;
      this.editingService = null;
      this.editForm = {
        nkrEnabled: false,
        webSelfServiceEnabled: false,
        specialOrderEnabled: false
      };
      this.saving = false;
    },

    async updateCarriersAndServices() {
      this.updatingServices = true;
      try {
        const response = await updateCarriersAndServicesFromShipStation();

        if (response.status === "success") {
          this.$toast.success("Services updated successfully from ShipStation");
          // Reload the carrier services to show the updated data
          await this.loadCarrierServices();
        } else {
          this.$toast.error(response.message || "Failed to update services");
        }
      } catch (error) {
        console.error("Error updating carriers and services:", error);
        this.$toast.error("Failed to update services from ShipStation");
      } finally {
        this.updatingServices = false;
      }
    },

    async saveService() {
      if (!this.editingService) return;

      this.saving = true;
      try {
        const updatedService = {
          uuid: this.editingService.uuid,
          nkrEnabled: this.editForm.nkrEnabled,
          webSelfServiceEnabled: this.editForm.webSelfServiceEnabled,
          specialOrderEnabled: this.editForm.specialOrderEnabled
        };

        const response = await updateCarrierService(updatedService);

        // Update the service in the local array with the response data
        const index = this.services.data.findIndex(
          s => s.uuid === this.editingService.uuid
        );
        if (index !== -1 && response.data) {
          // Use Vue.set to ensure reactivity or replace the entire object
          this.$set(this.services.data, index, {
            ...this.services.data[index],
            ...response.data
          });
        }

        this.$toast.success("Carrier service updated successfully");
        this.closeEditModal();
      } catch (error) {
        console.error("Error updating carrier service:", error);
        this.$toast.error("Failed to update carrier service");
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>

<style scoped>
.truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
